import {
  COMMERCIUM_CART_VIEWBOX_HEIGHT,
  COMMERCIUM_CART_VIEWBOX_PAD_X,
  COMMERCIUM_CART_VIEWBOX_PAD_Y,
  COMMERCIUM_CART_VIEWBOX_TOTAL_HEIGHT,
  COMMERCIUM_CART_VIEWBOX_TOTAL_WIDTH,
  COMMERCIUM_CART_VIEWBOX_WIDTH,
  commerciumCartShowSpokes,
  commerciumCartSpokeColor,
  commerciumCartSpokeStrokeWidth,
} from './commerciumCartMarkMetrics'

export const CART_RACE_PERIOD_S = 0.5
export const WHEEL_SPIN_PERIOD_S = 0.42
export const SPEED_LINE_PERIOD_S = 0.55

const CART_PATH = { x1: 16, y1: 20, cx: 100, cy: 114, x2: 184, y2: 20 }
const WHEELS = [
  { cx: 80, cy: 110, r: 12 },
  { cx: 120, cy: 110, r: 12 },
] as const
const PIVOT = { x: 100, y: 84 }

const CART_RACE_KEYFRAMES = [
  { p: 0, yPct: 0, rot: -5 },
  { p: 0.22, yPct: -7, rot: -8 },
  { p: 0.44, yPct: 0, rot: -4 },
  { p: 0.66, yPct: -4, rot: -7 },
  { p: 1, yPct: 0, rot: -5 },
] as const

const SPEED_LINES = [
  { yPct: 0.4, widthPct: 0.48, delay: 0 },
  { yPct: 0.55, widthPct: 0.56, delay: 0.13 },
  { yPct: 0.7, widthPct: 0.42, delay: 0.06 },
] as const

const LINES_REGION_WIDTH = COMMERCIUM_CART_VIEWBOX_WIDTH * 0.66
const LINE_RIGHT_INSET = 0.3

export function easeInOut(t: number): number {
  return t < 0.5 ? 2 * t * t : 1 - (-2 * t + 2) ** 2 / 2
}

function clamp01(value: number): number {
  return Math.min(1, Math.max(0, value))
}

/** Piecewise ease-in-out between CSS keyframes (matches cmmCartRace). */
export function sampleCartRace(timeS: number): { rotRad: number; translateY: number } {
  const cycle = (timeS % CART_RACE_PERIOD_S) / CART_RACE_PERIOD_S
  let i = 0
  for (; i < CART_RACE_KEYFRAMES.length - 1; i += 1) {
    if (cycle <= CART_RACE_KEYFRAMES[i + 1].p) break
  }
  const a = CART_RACE_KEYFRAMES[i]
  const b = CART_RACE_KEYFRAMES[i + 1]
  const span = b.p - a.p || 1
  const local = easeInOut(clamp01((cycle - a.p) / span))
  const yPct = a.yPct + (b.yPct - a.yPct) * local
  const rotDeg = a.rot + (b.rot - a.rot) * local
  return {
    rotRad: (rotDeg * Math.PI) / 180,
    translateY: (yPct / 100) * COMMERCIUM_CART_VIEWBOX_HEIGHT,
  }
}

export function sampleWheelRotation(timeS: number): number {
  return ((timeS % WHEEL_SPIN_PERIOD_S) / WHEEL_SPIN_PERIOD_S) * Math.PI * 2
}

type SpeedLineSample = {
  opacity: number
  translateRatio: number
  scaleX: number
}

/** Matches cmmSpeedZoom (translate % + scaleX from right origin). */
export function sampleSpeedLine(timeS: number, delayS: number): SpeedLineSample {
  const elapsed = timeS - delayS
  const cycle = ((elapsed % SPEED_LINE_PERIOD_S) + SPEED_LINE_PERIOD_S) % SPEED_LINE_PERIOD_S
  const t = cycle / SPEED_LINE_PERIOD_S

  let opacity = 0
  if (t <= 0.28) {
    opacity = (t / 0.28) * 0.95
  } else {
    opacity = 0.95 * (1 - (t - 0.28) / 0.72)
  }

  const translateRatio = 0.26 + (-1.65 - 0.26) * t
  const scaleX = 0.55 + (1.25 - 0.55) * t

  return { opacity, translateRatio, scaleX }
}

function createBrandGradient(
  ctx: CanvasRenderingContext2D,
  onDark: boolean
): CanvasGradient {
  const g = ctx.createLinearGradient(0, 0, COMMERCIUM_CART_VIEWBOX_WIDTH, COMMERCIUM_CART_VIEWBOX_HEIGHT * 0.35)
  if (onDark) {
    g.addColorStop(0, '#6FBFF4')
    g.addColorStop(0.5, '#0E7CBB')
    g.addColorStop(1, '#113D7E')
  } else {
    g.addColorStop(0, '#0D97D5')
    g.addColorStop(0.5, '#0E7CBB')
    g.addColorStop(1, '#113D7E')
  }
  return g
}

function drawSpeedLine(
  ctx: CanvasRenderingContext2D,
  timeS: number,
  line: (typeof SPEED_LINES)[number],
  lineColor: string
): void {
  const { opacity, translateRatio, scaleX } = sampleSpeedLine(timeS, line.delay)
  if (opacity <= 0.01) return

  const lineH = 4.2
  const lineW = LINES_REGION_WIDTH * line.widthPct
  const rightX = LINES_REGION_WIDTH * (1 - LINE_RIGHT_INSET)
  const y = line.yPct * COMMERCIUM_CART_VIEWBOX_HEIGHT - lineH / 2

  ctx.save()
  ctx.globalAlpha = opacity
  ctx.translate(rightX, y + lineH / 2)
  ctx.translate(lineW * translateRatio, 0)
  ctx.scale(scaleX, 1)
  ctx.translate(-lineW, -lineH / 2)

  const grad = ctx.createLinearGradient(0, 0, lineW, 0)
  grad.addColorStop(0, 'rgba(13,151,213,0)')
  grad.addColorStop(1, lineColor)
  ctx.fillStyle = grad
  roundRect(ctx, 0, 0, lineW, lineH, lineH / 2)
  ctx.fill()
  ctx.restore()
}

function roundRect(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  w: number,
  h: number,
  r: number
): void {
  const radius = Math.min(r, w / 2, h / 2)
  ctx.beginPath()
  ctx.moveTo(x + radius, y)
  ctx.lineTo(x + w - radius, y)
  ctx.quadraticCurveTo(x + w, y, x + w, y + radius)
  ctx.lineTo(x + w, y + h - radius)
  ctx.quadraticCurveTo(x + w, y + h, x + w - radius, y + h)
  ctx.lineTo(x + radius, y + h)
  ctx.quadraticCurveTo(x, y + h, x, y + h - radius)
  ctx.lineTo(x, y + radius)
  ctx.quadraticCurveTo(x, y, x + radius, y)
  ctx.closePath()
}

function drawSpokes(
  ctx: CanvasRenderingContext2D,
  cx: number,
  cy: number,
  angle: number,
  strokeWidth: number,
  color: string
): void {
  ctx.save()
  ctx.translate(cx, cy)
  ctx.rotate(angle)
  ctx.strokeStyle = color
  ctx.lineWidth = strokeWidth
  ctx.lineCap = 'round'
  const spokes: Array<[number, number, number, number]> = [
    [-7.5, 0, 7.5, 0],
    [-3.75, -6.5, 3.75, 6.5],
    [3.75, -6.5, -3.75, 6.5],
  ]
  spokes.forEach(([x1, y1, x2, y2]) => {
    ctx.beginPath()
    ctx.moveTo(x1, y1)
    ctx.lineTo(x2, y2)
    ctx.stroke()
  })
  ctx.restore()
}

function drawCartBody(ctx: CanvasRenderingContext2D, gradient: CanvasGradient): void {
  ctx.strokeStyle = gradient
  ctx.lineWidth = 21
  ctx.lineCap = 'round'
  ctx.beginPath()
  ctx.moveTo(CART_PATH.x1, CART_PATH.y1)
  ctx.quadraticCurveTo(CART_PATH.cx, CART_PATH.cy, CART_PATH.x2, CART_PATH.y2)
  ctx.stroke()
}

function drawWheel(
  ctx: CanvasRenderingContext2D,
  cx: number,
  cy: number,
  r: number,
  gradient: CanvasGradient,
  wheelAngle: number,
  cssSize: number,
  onDark: boolean
): void {
  ctx.fillStyle = gradient
  ctx.beginPath()
  ctx.arc(cx, cy, r, 0, Math.PI * 2)
  ctx.fill()

  if (commerciumCartShowSpokes(cssSize)) {
    drawSpokes(
      ctx,
      cx,
      cy,
      wheelAngle,
      commerciumCartSpokeStrokeWidth(cssSize),
      commerciumCartSpokeColor(cssSize, onDark)
    )
  }
}

export type DrawCartLoaderFrameOptions = {
  timeS: number
  cssWidth: number
  onDark?: boolean
  animate?: boolean
}

/** Paint one frame in viewBox coordinates (200×140). */
export function drawCartLoaderFrame(
  ctx: CanvasRenderingContext2D,
  options: DrawCartLoaderFrameOptions
): void {
  const { timeS, cssWidth, onDark = false, animate = true } = options
  const t = animate ? timeS : 0
  const lineColor = onDark ? '#6FBFF4' : '#0D97D5'
  const gradient = createBrandGradient(ctx, onDark)

  ctx.clearRect(0, 0, COMMERCIUM_CART_VIEWBOX_TOTAL_WIDTH, COMMERCIUM_CART_VIEWBOX_TOTAL_HEIGHT)

  ctx.save()
  ctx.translate(COMMERCIUM_CART_VIEWBOX_PAD_X, COMMERCIUM_CART_VIEWBOX_PAD_Y)

  SPEED_LINES.forEach((line) => drawSpeedLine(ctx, t, line, lineColor))

  const { rotRad, translateY } = sampleCartRace(t)
  const wheelAngle = sampleWheelRotation(t)

  ctx.save()
  ctx.translate(PIVOT.x, PIVOT.y)
  ctx.rotate(rotRad)
  ctx.translate(0, translateY)
  ctx.translate(-PIVOT.x, -PIVOT.y)

  drawCartBody(ctx, gradient)
  WHEELS.forEach(({ cx, cy, r }) => drawWheel(ctx, cx, cy, r, gradient, wheelAngle, cssWidth, onDark))

  ctx.restore()
  ctx.restore()
}

export function resolveCanvasPixelRatio(): number {
  if (typeof window === 'undefined') return 1
  return Math.min(Math.max(window.devicePixelRatio || 1, 1), 3)
}

export function sizeCanvasToCss(
  canvas: HTMLCanvasElement,
  cssWidth: number,
  cssHeight: number,
  dpr = resolveCanvasPixelRatio()
): CanvasRenderingContext2D | null {
  const w = Math.max(1, Math.round(cssWidth * dpr))
  const h = Math.max(1, Math.round(cssHeight * dpr))
  canvas.width = w
  canvas.height = h
  canvas.style.width = `${cssWidth}px`
  canvas.style.height = `${cssHeight}px`

  const ctx = canvas.getContext('2d')
  if (!ctx) return null

  ctx.setTransform(1, 0, 0, 1, 0, 0)
  ctx.clearRect(0, 0, w, h)

  const scale =
    Math.min(
      cssWidth / COMMERCIUM_CART_VIEWBOX_TOTAL_WIDTH,
      cssHeight / COMMERCIUM_CART_VIEWBOX_TOTAL_HEIGHT
    ) * dpr
  ctx.setTransform(scale, 0, 0, scale, 0, 0)
  ctx.imageSmoothingEnabled = true
  ctx.imageSmoothingQuality = 'high'

  return ctx
}

export function paintCartLoaderToCanvas(
  canvas: HTMLCanvasElement,
  options: DrawCartLoaderFrameOptions & { cssHeight: number }
): CanvasRenderingContext2D | null {
  const ctx = sizeCanvasToCss(
    canvas,
    options.cssWidth,
    options.cssHeight,
    resolveCanvasPixelRatio()
  )
  if (!ctx) return null
  drawCartLoaderFrame(ctx, options)
  return ctx
}
